/**
 * Admin Sidebar Component
 *
 * Navigation sidebar for the admin section.
 *
 * @fileoverview Admin sidebar with navigation links
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  VStack,
  Text,
  Icon,
  IconButton,
  Flex,
  HStack,
  Tooltip,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import {
  MdDashboard,
  MdBusiness,
  MdPeople,
  MdAdd,
  MdEmail,
  MdSend,
  MdPersonAdd,
  MdWarning,
  MdSchedule,
  MdAccountBalance,
  MdSmartToy,
  MdBlock,
} from 'react-icons/md';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { ThemeToggle } from '../ThemeToggle';

interface NavItemProps {
  icon: React.ElementType;
  label: string;
  to: string;
  isActive?: boolean;
  isCollapsed?: boolean;
  colorScheme?: 'default' | 'danger';
}

/**
 * Navigation item component
 */
const NavItem: React.FC<NavItemProps> = ({ icon, label, to, isActive, isCollapsed, colorScheme = 'default' }) => {
  const isDanger = colorScheme === 'danger';
  const activeBg = useColorModeValue(isDanger ? 'red.50' : 'blue.50', isDanger ? 'red.900' : 'blue.900');
  const activeColor = useColorModeValue(isDanger ? 'red.600' : 'blue.600', isDanger ? 'red.200' : 'blue.200');
  const hoverBg = useColorModeValue(isDanger ? 'red.50' : 'gray.100', isDanger ? 'whiteAlpha.100' : 'gray.700');
  const textColor = useColorModeValue(isDanger ? 'red.500' : 'gray.700', isDanger ? 'red.300' : 'gray.200');

  const content = (
    <Flex
      align="center"
      justify={isCollapsed ? 'center' : 'flex-start'}
      p={3}
      mx={2}
      borderRadius="md"
      cursor="pointer"
      bg={isActive ? activeBg : 'transparent'}
      color={isActive ? activeColor : textColor}
      fontWeight={isActive ? 'semibold' : 'normal'}
      _hover={{
        bg: isActive ? activeBg : hoverBg,
        color: isDanger ? 'red.600' : undefined,
      }}
      transition="all 0.2s"
      aria-current={isActive ? 'page' : undefined}
    >
      <Icon as={icon} boxSize={5} mr={isCollapsed ? 0 : 3} />
      {!isCollapsed && <Text fontSize="sm" noOfLines={1}>{label}</Text>}
    </Flex>
  );

  const link = (
    <Link to={to} style={{ width: '100%' }}>
      {content}
    </Link>
  );

  return isCollapsed ? (
    <Tooltip label={label} placement="right" hasArrow>
      {link}
    </Tooltip>
  ) : (
    link
  );
};

interface NavGroupProps {
  title: string;
  children: React.ReactNode;
  isCollapsed?: boolean;
}

/**
 * Navigation group component. Collapsed rails drop the uppercase title
 * (there's no room for it) and keep just a divider between groups.
 */
const NavGroup: React.FC<NavGroupProps> = ({ title, children, isCollapsed }) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box w="full">
      {!isCollapsed && (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={labelColor}
          textTransform="uppercase"
          letterSpacing="wider"
          px={5}
          py={2}
        >
          {title}
        </Text>
      )}
      <VStack align="stretch" spacing={1}>
        {children}
      </VStack>
    </Box>
  );
};

export interface AdminSidebarProps {
  /** Collapsed (icons-only) rail mode. Default: false. */
  isCollapsed?: boolean;
  /** Renders a collapse/expand toggle button in the footer when provided. */
  onToggleCollapse?: () => void;
}

/**
 * Admin Sidebar Component
 *
 * Displays navigation links for admin section.
 */
export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const brandColor = useColorModeValue('blue.600', 'blue.300');

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    // Check if current path starts with the target path
    // This handles nested routes like /admin/tenants/123
    return location.pathname.startsWith(path);
  };

  return (
    <Flex
      as="nav"
      direction="column"
      position="fixed"
      left={0}
      top={0}
      bottom={0}
      w="var(--xacto-sidebar-w)"
      bg={bgColor}
      borderRight="1px"
      borderColor={borderColor}
      transition="width 0.2s ease-in-out"
      overflowX="hidden"
    >
      {/* Logo/Brand */}
      <Flex
        h="16"
        flexShrink={0}
        align="center"
        justify={isCollapsed ? 'center' : 'flex-start'}
        px={isCollapsed ? 0 : 5}
        borderBottom="1px"
        borderColor={borderColor}
      >
        <Text fontSize="xl" fontWeight="bold" color={brandColor} noOfLines={1}>
          {isCollapsed ? 'X' : 'Xacto Admin'}
        </Text>
      </Flex>

      {/* Navigation */}
      <Box flex={1} overflowY="auto" overflowX="hidden">
      <VStack align="stretch" spacing={4} py={4}>
        {/* Main */}
        <NavGroup title="Main" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdDashboard}
            label="Dashboard"
            to="/admin"
            isActive={isActive('/admin') && !location.pathname.includes('/tenants')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* Monitoring */}
        <NavGroup title="Monitoring" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdAccountBalance}
            label="Connected Accounts"
            to="/admin/connected-accounts"
            isActive={isActive('/admin/connected-accounts')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            icon={MdWarning}
            label="Connection Issues"
            to="/admin/plaid-health"
            isActive={isActive('/admin/plaid-health')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            icon={MdSchedule}
            label="Scheduler Status"
            to="/admin/jobs-status"
            isActive={isActive('/admin/jobs-status')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* Tenants */}
        <NavGroup title="Tenants" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdBusiness}
            label="All Tenants"
            to="/admin/tenants"
            isActive={isActive('/admin/tenants') && !location.pathname.includes('/new')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            icon={MdAdd}
            label="Create Tenant"
            to="/admin/tenants/new"
            isActive={isActive('/admin/tenants/new')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* AI */}
        <NavGroup title="AI" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdSmartToy}
            label="AI Analysis"
            to="/admin/ai"
            isActive={isActive('/admin/ai')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* Invites */}
        <NavGroup title="Invites" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdEmail}
            label="All Invites"
            to="/admin/invites"
            isActive={isActive('/admin/invites') && !location.pathname.includes('/new')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            icon={MdSend}
            label="Send Invite"
            to="/admin/invites/new"
            isActive={isActive('/admin/invites/new')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* Users */}
        <NavGroup title="Users" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdPeople}
            label="All Users"
            to="/admin/users"
            isActive={isActive('/admin/users') && !location.pathname.includes('/invite')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            icon={MdPersonAdd}
            label="Invite User"
            to="/admin/users/invite"
            isActive={isActive('/admin/users/invite')}
            isCollapsed={isCollapsed}
          />
        </NavGroup>

        <Divider />

        {/* Danger Zone */}
        <NavGroup title="Danger Zone" isCollapsed={isCollapsed}>
          <NavItem
            icon={MdBlock}
            label="Remove Plaid Connection"
            to="/admin/plaid-removal"
            isActive={isActive('/admin/plaid-removal')}
            isCollapsed={isCollapsed}
            colorScheme="danger"
          />
        </NavGroup>

      </VStack>
      </Box>

      {/* Footer - Theme Toggle + collapse toggle */}
      <Box
        flexShrink={0}
        p={4}
        borderTop="1px"
        borderColor={borderColor}
      >
        <HStack justify={isCollapsed ? 'center' : 'space-between'} w="full">
          {!isCollapsed && (
            <Text fontSize="xs" color="gray.500">
              Theme
            </Text>
          )}
          <ThemeToggle size="sm" />
        </HStack>
        {onToggleCollapse && (
          <Tooltip
            label={isCollapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
            placement="right"
            hasArrow
          >
            <IconButton
              mt={2}
              w="full"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
              icon={
                <Icon
                  as={ChevronLeftIcon}
                  boxSize={4}
                  transform={isCollapsed ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s ease-in-out"
                />
              }
              size="sm"
              variant="ghost"
              onClick={onToggleCollapse}
            />
          </Tooltip>
        )}
      </Box>
    </Flex>
  );
};

export default AdminSidebar;
